import React, { useState, ReactNode, useRef } from "react";
import { Modal, Text, Group, Button, Alert } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { SubmissionType } from "types/userTypes";
import DeviceCreationForm, { ValidationResult } from "@components/submissions/deviceCreationForm";
import { ProductRequest } from "types/productTypes";
import { IconAlertCircle, IconCircleCheck } from "@tabler/icons-react";

interface CreateSubmissionModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (request: ProductRequest) => Promise<unknown>;
  submissionType: SubmissionType;
  targetOrgId: string;
  initialRequest?: ProductRequest | null;
  title?: string;
  description?: string;
}
const CreateSubmissionModal: React.FC<CreateSubmissionModalProps> = ({
  opened,
  onClose,
  title,
  description,
  onSubmit,
  submissionType,
  targetOrgId,
  initialRequest,
}) => {
  const { t } = useTranslation();
  const handleClose = () => {
    setSubmitError(null);
    setSubmitSuccess(false);
    setHasBeenWarned(false);
    setRequest(null);
    onClose();
  };

  const [request, setRequest] = useState<ProductRequest | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const formRef = useRef<{ validateDevice: () => Promise<ValidationResult> } | null>(null);
  const [hasBeenWarned, setHasBeenWarned] = useState(false);

  const getComponent = (): ReactNode => {
    if (submitSuccess) {
      return (
        <Alert color="green" icon={<IconCircleCheck size="1rem" />} mt="md" variant="light">
          {getSuccessMessage()}
        </Alert>
      );
    }

    switch (submissionType) {
      case SubmissionType.ADD_DEVICE:
        return (
          <DeviceCreationForm
            targetOrgId={targetOrgId}
            onProductRequestChange={setRequest}
            formRef={formRef}
          />
        );
      case SubmissionType.REMOVE_DEVICE:
        return null;
      default:
        return null;
    }
  };

  const getSuccessMessage = () => {
    switch (submissionType) {
      case SubmissionType.ADD_DEVICE:
        return t(
          "submissions.newDeviceSubmissionRequestModal.success",
          "The add device submission request was sent successfully.",
        );
      case SubmissionType.REMOVE_DEVICE:
        return t(
          "submissions.removeDeviceSubmissionRequestModal.success",
          "The remove device submission request was sent successfully.",
        );
      default:
        return t("success", "Success");
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    const requestToSubmit = request || initialRequest;
    const shouldValidate = submissionType === SubmissionType.ADD_DEVICE;

    try {
      if (!requestToSubmit) {
        return;
      }

      if (!shouldValidate) {
        await onSubmit(requestToSubmit);
        setSubmitSuccess(true);
        return;
      }

      if (!formRef.current) {
        setSubmitError(t("error"));
      } else {
        const validationResult = await formRef.current.validateDevice();
        switch (validationResult) {
          case ValidationResult.INVALID:
            setHasBeenWarned(false);
            console.warn("Validation failed, not submitting");
            break;
          case ValidationResult.WARNING:
            console.warn("Validation warning, proceeding with submission");
            if (hasBeenWarned) {
              setHasBeenWarned(false);
              await onSubmit(requestToSubmit);
              setSubmitSuccess(true);
            } else {
              setHasBeenWarned(true);
            }
            break;
          case ValidationResult.VALID: {
            await onSubmit(requestToSubmit);
            setHasBeenWarned(false);
            setSubmitSuccess(true);
            break;
          }
        }
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t("error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={title || t("submission.createNew")}
      size="md"
      padding="lg"
    >
      <Text>{description || t("submission.createNewDescription")}</Text>
      {submitError ? (
        <Alert color="red" icon={<IconAlertCircle size="1rem" />} mt="md" variant="light">
          {submitError}
        </Alert>
      ) : null}
      {getComponent()}
      <Group justify="flex-end" mt="md">
        {submitSuccess ? (
          <Button onClick={handleClose}>{t("common.close", "Close")}</Button>
        ) : (
          <>
            <Button variant="outline" onClick={handleClose}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSubmit} loading={isSubmitting} disabled={isSubmitting}>
              {t("submit")}
            </Button>
          </>
        )}
      </Group>
    </Modal>
  );
};

export default CreateSubmissionModal;
